import { useMemo } from 'react';

import qrcodegen from 'nayuki-qr-code-generator';

import { Flex } from 'components/uikit';
import { DepositMethod, DepositPostProcessStatus } from 'data/payments/models';

import { getBanksByMethod } from './BankDeeplinkButtons';
import { PaymentQrPostProcessStatus } from './PaymentQrPostProcessStatus';

const { QrCode, QrSegment } = qrcodegen;

const QR_BORDER = 1;
const ALPHANUMERIC_RE = /^[A-Z0-9 $%*+./:-]+$/;
const NUMERIC_RE = /^[0-9]+$/;
type SegMode = 'numeric' | 'alphanumeric' | 'byte';

// Segment bit cost for QR versions 10–26 (header 4 + count indicator).
const SEG_BITS = (mode: SegMode, len: number): number => {
  switch (mode) {
    case 'numeric':
      return 16 + Math.ceil((len * 10) / 3);
    case 'alphanumeric':
      return 15 + Math.ceil((len * 11) / 2);
    case 'byte':
      return 20 + len * 8;
  }
};

const makeOptimalSegments = (text: string): qrcodegen.QrSegment[] => {
  const n = text.length;
  const cost = Array<number>(n + 1).fill(Infinity);
  const back: { start: number; mode: SegMode }[] = [];
  cost[0] = 0;

  for (let i = 1; i <= n; i++) {
    for (let j = 0; j < i; j++) {
      const sub = text.slice(j, i);
      const modes: SegMode[] = ['byte'];
      if (ALPHANUMERIC_RE.test(sub)) modes.push('alphanumeric');
      if (NUMERIC_RE.test(sub)) modes.push('numeric');
      for (const mode of modes) {
        const c = (cost[j] ?? Infinity) + SEG_BITS(mode, i - j);
        if (c < (cost[i] ?? Infinity)) {
          cost[i] = c;
          back[i] = { start: j, mode };
        }
      }
    }
  }

  const segs: qrcodegen.QrSegment[] = [];
  for (let i = n; i > 0; ) {
    const b = back[i];
    if (!b) break;
    const sub = text.slice(b.start, i);
    segs.unshift(
      b.mode === 'numeric'
        ? QrSegment.makeNumeric(sub)
        : b.mode === 'alphanumeric'
        ? QrSegment.makeAlphanumeric(sub)
        : QrSegment.makeBytes(Array.from(new TextEncoder().encode(sub))),
    );
    i = b.start;
  }
  return segs;
};

type QrParams = {
  ecc: qrcodegen.QrCode.Ecc;
  version: number;
  mask: number;
  segments: (value: string) => qrcodegen.QrSegment[];
};

const QR_PARAMS: Partial<Record<DepositMethod, QrParams>> = {
  VND_TG_QR: {
    ecc: QrCode.Ecc.LOW,
    version: 6,
    mask: 3,
    segments: QrSegment.makeSegments,
  },
  VND_TG_FTD: {
    ecc: QrCode.Ecc.LOW,
    version: 6,
    mask: 3,
    segments: QrSegment.makeSegments,
  },
  VND_TG_VTB: {
    ecc: QrCode.Ecc.LOW,
    version: 6,
    mask: 3,
    segments: QrSegment.makeSegments,
  },
  VND_TG_GAZPROM: {
    ecc: QrCode.Ecc.LOW,
    version: 6,
    mask: 3,
    segments: QrSegment.makeSegments,
  },
  VND_TG_ALFA: {
    ecc: QrCode.Ecc.LOW,
    version: 6,
    mask: 3,
    segments: QrSegment.makeSegments,
  },
  IDR_TG_QR: {
    ecc: QrCode.Ecc.MEDIUM,
    version: 11,
    mask: 4,
    segments: makeOptimalSegments,
  },
  PHP_TG_QR: {
    ecc: QrCode.Ecc.MEDIUM,
    version: 11,
    mask: 4,
    segments: makeOptimalSegments,
  },
  PHP_TG_GAZPROM: {
    ecc: QrCode.Ecc.MEDIUM,
    version: 11,
    mask: 4,
    segments: makeOptimalSegments,
  },
  BRA_TG_QR: {
    ecc: QrCode.Ecc.MEDIUM,
    version: 11,
    mask: 4,
    segments: makeOptimalSegments,
  },
  PIX: {
    ecc: QrCode.Ecc.MEDIUM,
    version: 11,
    mask: 4,
    segments: makeOptimalSegments,
  },
  TJS_TG: {
    ecc: QrCode.Ecc.MEDIUM,
    version: 11,
    mask: 4,
    segments: QrSegment.makeSegments,
  },
  TJS_TG_GAZPROM: {
    ecc: QrCode.Ecc.MEDIUM,
    version: 11,
    mask: 4,
    segments: QrSegment.makeSegments,
  },
  ALFA_QR: {
    ecc: QrCode.Ecc.LOW,
    version: 11,
    mask: 4,
    segments: QrSegment.makeSegments,
  },
  NSPK_QR: {
    ecc: QrCode.Ecc.LOW,
    version: 6,
    mask: 3,
    segments: QrSegment.makeSegments,
  },
};

const getQrByMethod = (method: DepositMethod, value: string) => {
  const p = QR_PARAMS[method];
  if (!p)
    return QrCode.encodeSegments(QrSegment.makeSegments(value), QrCode.Ecc.LOW);
  return QrCode.encodeSegments(
    p.segments(value),
    p.ecc,
    p.version,
    p.version,
    p.mask,
    false,
  );
};

const generateQrSvgPath = (value: string, method: DepositMethod | null) => {
  const qr = getQrByMethod(method ?? 'VND_TG_QR', value);
  const { size } = qr;
  const viewBox = size + QR_BORDER * 2;

  const path = Array.from({ length: size }, (_, y) =>
    Array.from({ length: size }, (_, x) =>
      qr.getModule(x, y) ? `M${x + QR_BORDER},${y + QR_BORDER}h1v1h-1z` : '',
    ).join(''),
  ).join('');

  return { path, viewBox };
};

interface DownloadQrOptions {
  canvasWidth?: number;
  canvasHeight?: number;
  qrWidthRatio?: number;
  qrHeightRatio?: number;
  offsetXRatio?: number;
  offsetYRatio?: number;
  // Canvas background; the QR itself always stays black on a white pad
  backgroundColor?: string;
  // Place the QR in the middle of the canvas, ignoring offset ratios
  center?: boolean;
}

export const downloadQrAsPng = (
  value: string,
  fileName = 'qr.png',
  method: DepositMethod,
  {
    canvasWidth = 1000,
    canvasHeight = 1000,
    qrWidthRatio = 0.25,
    qrHeightRatio = 0.25,
    offsetXRatio = 0.07,
    offsetYRatio = 0.3,
    backgroundColor = '#fff',
    center = false,
  }: DownloadQrOptions = {},
) => {
  const qr = getQrByMethod(method, value);
  const targetQrSize = Math.min(
    Math.floor(canvasWidth * qrWidthRatio),
    Math.floor(canvasHeight * qrHeightRatio),
  );
  const scale = Math.max(1, Math.floor(targetQrSize / qr.size));
  const drawnSize = qr.size * scale;
  const offsetX = center
    ? Math.floor((canvasWidth - drawnSize) / 2)
    : Math.floor(canvasWidth * offsetXRatio);
  const offsetY = center
    ? Math.floor((canvasHeight - drawnSize) / 2)
    : Math.floor(canvasHeight * offsetYRatio);

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // White quiet zone around the QR so it stays scannable on a dark background
  const quietZone = scale * 4;
  ctx.fillStyle = '#fff';
  ctx.fillRect(
    offsetX - quietZone,
    offsetY - quietZone,
    qr.size * scale + quietZone * 2,
    qr.size * scale + quietZone * 2,
  );
  ctx.fillStyle = '#000';

  Array.from({ length: qr.size }).forEach((_, y) => {
    Array.from({ length: qr.size }).forEach((_, x) => {
      if (qr.getModule(x, y)) {
        ctx.fillRect(offsetX + x * scale, offsetY + y * scale, scale, scale);
      }
    });
  });

  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, 'image/png');
};

interface BankDeeplinkQRProps {
  id: string;
  method: DepositMethod | null;
  fromBank: string | null;
  value?: string | null;
  size?: number;
  postProcessStatus?: DepositPostProcessStatus | null;
}

export const BankDeeplinkQR = ({
  id,
  method,
  fromBank,
  value,
  size = 200,
  postProcessStatus,
}: BankDeeplinkQRProps) => {
  const availableBanks = getBanksByMethod(method, fromBank);
  const qrValue = value ?? `${window.location.href}/deeplink`;

  const qrData = useMemo(
    () => generateQrSvgPath(qrValue, method),
    [qrValue, method],
  );

  if (postProcessStatus)
    return (
      <PaymentQrPostProcessStatus
        id={id}
        method={method}
        status={postProcessStatus}
        size={size}
      />
    );

  if (
    method !== 'NSPK_QR' &&
    method !== 'PIX' &&
    method !== 'KGS_ELQR' &&
    availableBanks.length !== 1
  )
    return null;

  return (
    <Flex>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${qrData.viewBox} ${qrData.viewBox}`}
        shapeRendering='crispEdges'
      >
        <rect width='100%' height='100%' fill='#fff' />
        <path d={qrData.path} fill='#000' />
      </svg>
    </Flex>
  );
};
